import type { ContactContextField, EnrichmentBlock, WorkflowNode, WorkflowEdge } from '@/types/workflow'
import { ENRICHMENT_BLOCK_TO_FIELD, PROVIDER_FIELD_MAP } from './enrichmentCatalog'

// Re-export for backward compatibility. The block -> field and provider -> field
// mappings now live in enrichmentCatalog.ts (the single source of truth shared
// with ContactFieldSelector and NodeConfigPanel) so the three lists can't drift.
export { ENRICHMENT_BLOCK_TO_FIELD }

/**
 * Walk the workflow graph backward from `nodeId` and collect the contact
 * fields that will be populated by all upstream enrichContacts nodes.
 *
 * The start node itself is excluded (a node cannot be upstream of itself).
 * Supports multiple upstream enrichContacts nodes — returns the union.
 */
export function getUpstreamEnrichmentFields(
  nodeId: string,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
): ContactContextField[] {
  // Build reverse adjacency: target → [source, ...]
  const reverseAdj: Record<string, string[]> = {}
  for (const e of edges) {
    if (!reverseAdj[e.target]) reverseAdj[e.target] = []
    reverseAdj[e.target].push(e.source)
  }

  const nodeMap = new Map(nodes.map(n => [n.id, n]))

  // Seed visited with nodeId so the current node is never inspected
  const visited = new Set<string>([nodeId])
  const queue: string[] = [...(reverseAdj[nodeId] || [])]
  const fields: ContactContextField[] = []

  while (queue.length) {
    const current = queue.shift()!
    if (visited.has(current)) continue
    visited.add(current)

    const node = nodeMap.get(current)
    if (node?.type === 'enrichContacts') {
      // Map enabled_blocks → contact fields
      const blocks: EnrichmentBlock[] = node.data?.enrich_contacts_config?.enabled_blocks || []
      for (const block of blocks) {
        const field = ENRICHMENT_BLOCK_TO_FIELD[block]
        if (field && !fields.includes(field)) {
          fields.push(field)
        }
      }

      // Map enabled providers → their contact fields. Providers are tracked
      // separately in config.providers, not enabled_blocks. PROVIDER_FIELD_MAP
      // is derived from the shared enrichment catalog, so every provider a user
      // can enable (including News) surfaces its field here.
      const providers: Array<string | { name: string }> =
        node.data?.enrich_contacts_config?.providers || []
      for (const p of providers) {
        const name = typeof p === 'string' ? p : p.name
        for (const f of PROVIDER_FIELD_MAP[name] || []) {
          if (!fields.includes(f)) fields.push(f)
        }
      }
    }

    for (const parentId of reverseAdj[current] || []) {
      queue.push(parentId)
    }
  }

  return fields
}

/**
 * Walk the workflow graph backward from `nodeId` and collect the material ids
 * selected by all upstream generateDeck ("Collaterals") nodes.
 *
 * Collects both modes:
 *  - AI Generate mode: generate_deck_config.material_ids (array)
 *  - Attach Direct mode: generate_deck_config.attached_material_id (single id)
 *
 * The start node itself is excluded (seeded into `visited`). Returns a
 * de-duplicated, first-seen-ordered array. Union across all upstream
 * Collaterals nodes.
 */
export function getUpstreamCollateralMaterialIds(
  nodeId: string,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
): number[] {
  // Build reverse adjacency: target → [source, ...]
  const reverseAdj: Record<string, string[]> = {}
  for (const e of edges) {
    if (!reverseAdj[e.target]) reverseAdj[e.target] = []
    reverseAdj[e.target].push(e.source)
  }

  const nodeMap = new Map(nodes.map(n => [n.id, n]))

  // Seed visited with nodeId so the current node is never inspected
  const visited = new Set<string>([nodeId])
  const queue: string[] = [...(reverseAdj[nodeId] || [])]
  const ids: number[] = []
  const seen = new Set<number>()

  const add = (id: number | undefined | null) => {
    if (typeof id === 'number' && !seen.has(id)) {
      seen.add(id)
      ids.push(id)
    }
  }

  while (queue.length) {
    const current = queue.shift()!
    if (visited.has(current)) continue
    visited.add(current)

    const node = nodeMap.get(current)
    if (node?.type === 'generateDeck') {
      const cfg = node.data?.generate_deck_config
      // Only what this node's CURRENT mode will actually put on the email.
      // Both fields persist across a mode switch — deliberately, so flipping
      // back does not lose a selection — so reading both meant a node moved
      // to AI Generate still handed the downstream email the material left
      // behind in Attach Direct (AB#22383). The predicate mirrors
      // GenerateDeckConfig and GenerateDeckNode: anything that is not
      // attach_direct is AI Generate, which is also what a node saved before
      // `mode` existed should be treated as.
      if (cfg?.mode === 'attach_direct') {
        add(cfg?.attached_material_id)
      } else {
        for (const id of cfg?.material_ids || []) add(id)
      }
    }

    for (const parentId of reverseAdj[current] || []) {
      queue.push(parentId)
    }
  }

  return ids
}
